import React, {useEffect, useMemo} from "react"
import {SchoolClassCourseModule, User} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import {Text, View} from "react-native"
import AppHistory from "shared/history"
import {digg} from "diggerize"
import EventEmitter from "events"
import findOrCreateTeamSurveyStep from "components/survey-steps/find-or-create-team-survey-step"
import Link from "@kaspernj/api-maker/build/link"
import Logger from "@kaspernj/api-maker/build/logger"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import SurveysBottomBar from "components/surveys/bottom-bar"
import SurveysPageContainer from "components/surveys/page-container"
import SurveyStepsPresentation from "components/survey-steps/presentation"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useHiddenTawkWidget from "shared/use-hidden-tawk-widget"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import useNow from "set-state-compare/build/use-now"
import {useParams} from "@kaspernj/api-maker/build/router/route"

/** @typedef {object} RoutesUserSchoolClassCourseModulesProps */

/**
 * @typedef {object} RoutesUserSchoolClassCourseModulesState
 * @property {unknown} teamSurveyStep
 */

/**
 * @extends {ShapeComponent<RoutesUserSchoolClassCourseModulesProps, RoutesUserSchoolClassCourseModulesState>}
 */
class RoutesUserSchoolClassCourseModules extends ShapeComponent {
  /** @type {RoutesUserSchoolClassCourseModulesState} */
  state = {
    teamSurveyStep: undefined
  }

  teamSurveyStepLoadRequestId = 0

  componentDidUpdate(prevProps, prevState) {
    const prevSurveyStepId = prevState?.teamSurveyStep?.surveyStep()?.id()
    const nextSurveyStepId = this.s.teamSurveyStep?.surveyStep()?.id()
    const prevTeamSurveyStepId = prevState?.teamSurveyStep?.id()
    const nextTeamSurveyStepId = this.s.teamSurveyStep?.id()

    if (prevSurveyStepId != nextSurveyStepId || prevTeamSurveyStepId != nextTeamSurveyStepId) {
      this.updateLayout()
    }
  }

  setup() {
    setLayout("user")

    this.logger ||= new Logger({name: "RoutesUserSchoolClassCourseModules"})
    this.logger.setDebug(false)


    const {t} = useI18n({namespace: "js.routes.user.school_class_course_modules.show"})
    const currentUser = useCurrentUser()
    const frontLayout = useFrontLayout()
    const params = useParams()
    const {schoolClassCourseModule, schoolClassCourseModuleId} = useModel(
      SchoolClassCourseModule,
      {
        match: {params},
        preload: [
          "school_class_course.course.brain_break",
          "school_class_course.course.challenge",
          "school_class_course.course.course_groups",
          "school_class_course.course.visible_course_modules",
          "school_class_course.school_class",
          "course_module",
          "team_survey.survey"
        ],
        select: {
          Course: ["backgroundImageUrl", "id"],
          CourseGroup: ["identifier"],
          CourseModule: ["id", "position"],
          SchoolClass: ["id", "schoolId"],
          SchoolClassCourse: ["courseId", "id", "schoolClassId", "state"],
          SchoolClassCourseModule: ["id", "state"],
          Survey: ["surveyStepsCount"],
          TeamSurvey: ["id", "preview", "schoolClassCheckInId", "state"]
        }
      }
    )
    const loadedSchoolClassCourseModule = schoolClassCourseModule?.id() == schoolClassCourseModuleId ? schoolClassCourseModule : undefined
    const schoolClassCourse = loadedSchoolClassCourseModule?.schoolClassCourse()
    const course = schoolClassCourse?.course()
    const courseModule = loadedSchoolClassCourseModule?.courseModule()
    const survey = loadedSchoolClassCourseModule?.teamSurvey()?.survey()
    const teamSurvey = loadedSchoolClassCourseModule?.teamSurvey()
    const teamSurveyStep = this.s.teamSurveyStep?.teamSurveyId() == teamSurvey?.id() ? this.s.teamSurveyStep : undefined
    const surveyStep = teamSurveyStep?.surveyStep()
    const teamSurveyStepId = digg(params, "team_survey_step_id")

    this.course = course
    this.courseModule = courseModule
    this.currentUser = currentUser
    this.frontLayout = frontLayout
    this.schoolClassCourse = schoolClassCourse
    this.schoolClassCourseModule = loadedSchoolClassCourseModule
    this.schoolClassCourseModuleId = schoolClassCourseModuleId
    this.survey = survey
    this.surveyStep = surveyStep
    this.surveyStepsPresentationEvents = useMemo(() => new EventEmitter(), [])
    this.t = t
    this.teamSurvey = teamSurvey
    this.teamSurveyStep = teamSurveyStep
    this.teamSurveyStepId = teamSurveyStepId

    // Wait for useModel to catch up to the route params before loading a step.
    const readyKey = loadedSchoolClassCourseModule ? loadedSchoolClassCourseModule.id() : undefined

    useNow(() => {
      if (currentUser && readyKey) this.loadTeamSurveyStep()
    }, [currentUser?.id(), readyKey, teamSurveyStepId])

    useHiddenTawkWidget()

    useEffect(() => {
      this.updateLayout()
    }, [
      course?.id(),
      frontLayout,
      schoolClassCourse?.id(),
      schoolClassCourseModule?.id(),
      this.s.teamSurveyStep?.id(),
      surveyStep?.id()
    ])
  }

  loadTeamSurveyStep = async () => {
    const requestId = this.teamSurveyStepLoadRequestId + 1

    this.teamSurveyStepLoadRequestId = requestId

    try {
      const teamSurveyStep = await findOrCreateTeamSurveyStep({
        teamSurveyId: this.tt.schoolClassCourseModule.teamSurvey().id(),
        teamSurveyStepId: this.tt.teamSurveyStepId,
        withQuery: ({query}) => query.ransack({team_survey_school_class_course_module_id_eq: this.tt.schoolClassCourseModuleId})
      })

      if (!this.isMounted() || requestId != this.teamSurveyStepLoadRequestId) return

      this.setState({teamSurveyStep}, () => {
        this.updateLayout()
      })
    } catch (error) {
      if (!this.isMounted() || requestId != this.teamSurveyStepLoadRequestId) {
        this.logger.error([
          "Ignoring stale teamSurveyStep load error after route change",
          error,
          {
            currentRequestId: this.teamSurveyStepLoadRequestId,
            requestId,
            schoolClassCourseModuleId: this.tt.schoolClassCourseModuleId,
            teamSurveyStepId: this.tt.teamSurveyStepId
          }
        ])

        return
      }

      throw error
    }
  }

  render() {
    const {schoolClassCourseModule, surveyStep, surveyStepsPresentationEvents, teamSurvey, teamSurveyStep} = this.tt
    const teamSurveyExpired = teamSurvey?.state() == "expired"
    // Self-paced modules (no check-in) can be restarted with a fresh attempt; check-in surveys keep the
    // return-only expired view since their live session can't be re-run.
    const canRestartExpiredModule = teamSurveyExpired &&
      !teamSurvey?.schoolClassCheckInId() &&
      schoolClassCourseModule?.state() != "completed"

    return (
      <View
        dataSet={this.cache("rootViewDataSet", {
          route: "user/school-class-course-modules/show",
          surveyStepId: surveyStep?.id(),
          teamSurveyStepId: teamSurveyStep?.id()
        }, [surveyStep?.id(), teamSurveyStep?.id()])}
      >
        {schoolClassCourseModule && teamSurveyExpired &&
          <View
            style={this.expiredContainerStyle ||= {alignItems: "center", padding: 32}}
            testID="team-survey-expired"
          >
            <Text
              style={this.expiredTitleStyle ||= {color: "white", fontSize: 24, marginBottom: 16, textAlign: "center"}}
              testID="team-survey-expired-title"
            >
              {this.t(".module_expired_title")}
            </Text>
            <Text
              style={this.expiredBodyStyle ||= {color: "white", marginBottom: 24, textAlign: "center"}}
              testID="team-survey-expired-body"
            >
              {this.t(".module_expired_body")}
            </Text>
            {canRestartExpiredModule &&
              <Text
                onPress={this.tt.onStartModuleAgainClicked}
                style={this.expiredStartAgainStyle ||= {color: "white", marginBottom: 16, textDecorationLine: "underline"}}
                testID="team-survey-expired-start-again-link"
              >
                {this.t(".start_again")}
              </Text>
            }
            <Link testID="team-survey-expired-return-link" to={this.expiredReturnPath()}>
              <Text
                style={this.expiredLinkStyle ||= {color: "white", textDecorationLine: "underline"}}
                testID="team-survey-expired-return-link-text"
              >
                {this.t(".return_to_class_space")}
              </Text>
            </Link>
          </View>
        }
        {schoolClassCourseModule && surveyStep && teamSurveyStep && !teamSurveyExpired &&
          <div className="content-container">
            <SurveysPageContainer>
              <SurveyStepsPresentation
                events={surveyStepsPresentationEvents}
                key={`survey-steps-presentation-${schoolClassCourseModule.id()}-${surveyStep.id()}`}
                onAnswersSubmitted={this.tt.onAnswersSubmitted}
                schoolClass={schoolClassCourseModule.schoolClassCourse().schoolClass()}
                submitButton={false}
                surveyStep={surveyStep}
                teamSurvey={teamSurvey}
                teamSurveyStep={teamSurveyStep}
              />
            </SurveysPageContainer>
          </div>
        }
      </View>
    )
  }

  bottomBar() {
    const {schoolClassCourseModule, teamSurveyStep} = this.tt
    const surveyStepFromTeam = teamSurveyStep?.surveyStep()
    const teamSurveyExpired = schoolClassCourseModule?.teamSurvey()?.state() == "expired"

    return (
      <>
        {schoolClassCourseModule && surveyStepFromTeam && teamSurveyStep && !teamSurveyExpired &&
          <SurveysBottomBar
            color="white"
            onCancelClicked={this.tt.onCancelClicked}
            onEndClicked={this.tt.onEndClicked}
            onNextStepClicked={this.tt.onNextStepClicked}
            onPreviousStepClicked={this.tt.onPreviousStepClicked}
            survey={schoolClassCourseModule.teamSurvey().survey()}
            surveyStep={surveyStepFromTeam}
            teamSurvey={schoolClassCourseModule.teamSurvey()}
            teamSurveyStep={teamSurveyStep}
          />
        }
      </>
    )
  }

  updateLayout() {
    const {course, frontLayout, schoolClassCourse, teamSurveyStep} = this.tt
    const surveyStepFromTeam = teamSurveyStep?.surveyStep()

    frontLayout?.setState({
      background: course?.backgroundImageUrl(),
      bottomBar: this.bottomBar(),
      contentCentered: true,
      darkBackground: true,
      headTitle: surveyStepFromTeam?.name(),
      logo: false,
      menu: false,
      schoolClass: schoolClassCourse?.schoolClass(),
      proTrialTopBar: false,
      userMenu: false,
      weekBar: false
    })
  }

  onStartModuleAgainClicked = async () => {
    const {courseModule, schoolClassCourse} = this.tt
    const result = await schoolClassCourse.startModule({course_module_id: courseModule.id()})
    const schoolClassCourseModule = digg(result, "school_class_course_module")
    const teamSurveyStep = digg(result, "team_survey_step_to_redirect_to")

    AppHistory.push(Routes.userSchoolClassCourseModuleStepPath(schoolClassCourseModule.id(), teamSurveyStep.id()))
  }

  endModule = async () => {
    const {schoolClassCourse, schoolClassCourseModule, teamSurvey} = this.tt
    const promises = []

    if (this.isLastStep() && schoolClassCourseModule.state() != "completed" && !teamSurvey.preview()) {
      promises.push(
        User.stateEvent({model: schoolClassCourseModule, state_event: "complete", ignore_if_already_in_target_state: true})
      )
    }

    if (this.isLastStep() && teamSurvey.state() == "new") {
      promises.push(
        User.stateEvent({model: teamSurvey, state_event: "complete", ignore_if_already_in_target_state: true})
      )
    }

    if (this.isLastModule() && schoolClassCourse.state() != "completed" && !teamSurvey.preview()) {
      promises.push(
        User.stateEvent({model: schoolClassCourse, state_event: "complete", ignore_if_already_in_target_state: true})
      )
    }

    await Promise.all(promises)
    this.redirectBack()
  }

  redirectBack() {
    const {schoolClassCourse} = this.tt

    if (this.redirectToClassSpace()) {
      AppHistory.push(Routes.userSchoolClassClassSpacePath(schoolClassCourse.schoolClassId()))
    } else {
      AppHistory.push(Routes.userCoursePath(schoolClassCourse.courseId(), {school_class_course_id: schoolClassCourse.id()}))
    }
  }

  expiredReturnPath = () => {
    const {schoolClassCourse} = this.tt

    if (this.redirectToClassSpace()) return Routes.userSchoolClassClassSpacePath(schoolClassCourse.schoolClassId())

    return Routes.userCoursePath(schoolClassCourse.courseId(), {school_class_course_id: schoolClassCourse.id()})
  }

  redirectToClassSpace() {
    const {course} = this.tt
    let redirectToClassSpace = false

    if (this.tt.visibleCourseModules().length == 1) {
      const courseGroupIdentifiers = course.courseGroups().loaded().map((courseGroup) => courseGroup.identifier())

      if (course.brainBreak() || course.challenge() || courseGroupIdentifiers.includes("brain_break") || courseGroupIdentifiers.includes("challenge")) {
        redirectToClassSpace = true
      }
    }

    return redirectToClassSpace
  }

  visibleCourseModules() {
    const {course} = this.tt

    if (!course) return []

    return course.visibleCourseModules().loaded()
  }

  isLastModule = () => {
    const visibleCourseModules = this.tt.visibleCourseModules().slice().sort((courseModule1, courseModule2) =>
      courseModule1.position() - courseModule2.position()
    )
    const lastVisibleCourseModule = visibleCourseModules[visibleCourseModules.length - 1]

    if (lastVisibleCourseModule && this.tt.courseModule.id() == lastVisibleCourseModule.id()) return true
  }

  isLastStep = () => {
    if (this.s.teamSurveyStep.surveyStep().position() >= this.tt.survey.surveyStepsCount()) return true
  }

  onAnswersSubmitted = ({after, nextTeamSurveyStep}) => {
    if (after == "end") {
      this.endModule()
    } else if (after == "nextStep") {
      this.redirectToNextStep({nextTeamSurveyStep})
    } else if (after == "previousStep") {
      this.redirectToPreviousStep()
    } else {
      throw new Error(`Unhanded submit mode: ${after}`)
    }
  }

  onCancelClicked = () => this.redirectBack()
  onEndClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "end"})
  onNextStepClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "nextStep"})
  onPreviousStepClicked = () => this.tt.surveyStepsPresentationEvents.emit("submit", {after: "previousStep"})

  redirectToNextStep = ({nextTeamSurveyStep}) => {
    AppHistory.push(Routes.userSchoolClassCourseModuleStepPath(this.tt.schoolClassCourseModule.id(), nextTeamSurveyStep.id()))
  }

  redirectToPreviousStep = async () => {
    const previousStepResult = await this.s.teamSurveyStep.previousStep()
    const previousTeamSurveyStep = digg(previousStepResult, "previous_team_survey_step")

    AppHistory.push(Routes.userSchoolClassCourseModuleStepPath(this.tt.schoolClassCourseModule.id(), previousTeamSurveyStep.id()))
  }
}

export default memo(shapeComponent(RoutesUserSchoolClassCourseModules))
